import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import CustomerInvoicePreview from "layouts/Invoices/CustomerInvoicePreview";
import RCTIDocument from "layouts/RCTI/RCTIDocument";
import { appConstants } from "./appConstants";
import "layouts/Quotation/quotation.css";
import "layouts/Invoices/invoiceFlow.css";
import "layouts/RCTI/rctiDetail.css";

const number = (value) =>
  Number(String(value ?? 0).replace(/[^0-9.-]/g, "")) || 0;
const dateText = (
  value,
  options = { day: "numeric", month: "short", year: "numeric" }
) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "\u2014"
    : new Intl.DateTimeFormat("en-AU", options).format(date);
};

export function renderDocumentContent(type, values, id, login, dark) {
  const items = values.itemsList || values.items || [];
  const currency = String(
    [
      values.pdfCurrencyCode,
      values.currencyCode,
      login.currencyCode,
      values.currencyValue,
    ].find((value) => /^[A-Z]{3}$/i.test(String(value || ""))) || "AUD"
  ).toUpperCase();
  const organisation =
    values.businessCompanyName ||
    login.companyName ||
    login.businessName ||
    login.organisationName ||
    login.organizationName ||
    "Invoice & Recover";
  const businessLogo = values.defaultLogoFileName
    ? `${appConstants.baseURL}employee/viewFile?fileName=${encodeURIComponent(
        values.defaultLogoFileName
      )}`
    : "";
  let content;
  if (type === "quotation") {
    content = (
      <div className={`invoice-flow-shell${dark ? " dark" : ""}`}>
        <div className="invoice-flow-main">
          <CustomerInvoicePreview
            documentType="quote"
            organisation={organisation}
            businessLogo={businessLogo}
            invoiceNumber={values.quotationNumber || "Auto generated"}
            status={values.quotationStatus || "Draft"}
            customerName={values.customerCompanyName || values.name || values.contactName || "Customer"}
            customerDetails={{
              emailAddress: values.customerEmailAddress || values.recipientEmail,
              streetAddress: values.customerStreetAddress,
            }}
            invoiceDate={values.quotationDate}
            dueDate={values.validUptoDate}
            dueUpto={values.validUpto}
            currency={currency}
            gstType={values.gstType}
            items={items}
            purchaseOrderNumber={values.purchaseOrderNumber}
            scopeNotes={values.paymentInstruction}
            invoiceNotes={values.terms}
            bankAccountName={values.bankAccountName}
            bankBsbCode={values.bankBsbCode}
            bankAccountNumber={values.bankAccountNumber}
            subtotal={values.totalAmount}
            totalDiscounts={values.totalDiscountAmount}
            totalGST={values.totalGSTAmount}
            grandTotal={values.finalAmount}
            isSaved
            topBarVariant="none"
            darkMode={dark}
          />
        </div>
      </div>
    );
  } else if (type === "invoice") {
    const paymentsReceived = (values.invoicePayment || []).reduce(
      (sum, payment) =>
        sum + number(payment.paymentAmount ?? payment.amount ?? 0),
      0
    );
    const sent =
      Number(values.emailSent || 0) > 0 ||
      String(values.invoiceStatus).toLowerCase() === "paid";
    content = (
      <div className={`invoice-flow-shell${dark ? " dark" : ""}`}>
        <div className="invoice-flow-main">
          <CustomerInvoicePreview
            businessLogo={businessLogo}
            organisation={organisation}
            invoiceNumber={values.invoiceNumber}
            status={
              sent
                ? values.invoiceStatus || "Issued"
                : "Draft \u00b7 Preview only"
            }
            customerName={
              values.customerCompanyName || values.contactName || "Customer"
            }
            customerDetails={{
              emailAddress:
                values.customerEmailAddress || values.recipientEmail,
              streetAddress: values.customerStreetAddress,
            }}
            invoiceDate={values.invoiceDate}
            dueDate={values.dueDate}
            dueUpto={values.dueUpto || values.validUpto || "14 days"}
            currency={currency}
            gstType={values.gstType}
            items={items}
            quotationNumber={values.quotationNumber}
            purchaseOrderNumber={values.purchaseOrderNumber}
            scopeNotes={values.scopeNotes || ""}
            invoiceNotes={values.terms}
            paymentInstructions={values.paymentInstruction}
            bankAccountName={values.bankAccountName}
            bankBsbCode={values.bankBsbCode}
            bankAccountNumber={values.bankAccountNumber}
            subtotal={values.totalAmount}
            totalDiscounts={values.totalDiscountAmount}
            totalGST={values.totalGSTAmount}
            grandTotal={values.finalAmount}
            payablePendingAmount={values.payablePendingAmount}
            paymentsReceived={paymentsReceived}
            isSaved
            topBarVariant="none"
            darkMode={dark}
            onBack={() => {}}
          />
        </div>
      </div>
    );
  } else {
    const gstRates = [
      ...new Set(
        items
          .map((item) =>
            String(item.gst || "")
              .trim()
              .replace(/^GST\s*/i, "")
              .replace(/%$/, "")
          )
          .filter((rate) => /\d/.test(rate))
          .map((rate) => `${rate}%`)
      ),
    ];
    content = (
      <div
        className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell${
          dark ? " dark" : ""
        }`}
      >
        <div className="invoice-flow-main">
          <main className="invoice-detail-page rcti-detail-page">
            <div className="rcti-detail-layout">
              <RCTIDocument
                values={values}
                items={items}
                documents={values.documentsList || []}
                organisation={
                  values.businessName ||
                  login.businessName ||
                  login.businessUserName ||
                  "Your business"
                }
                rctiID={id}
                loadingPage={false}
                currencyCode={currency}
                subtotalExcludingGst={
                  number(values.totalAmount) - number(values.totalGSTAmount)
                }
                gstSummaryLabel={
                  gstRates.length
                    ? `GST ${gstRates.join(", ")} (included)`
                    : "GST included"
                }
                total={number(values.finalAmount || values.totalAmount)}
                money={(value) =>
                  new Intl.NumberFormat("en-AU", {
                    style: "currency",
                    currency,
                  }).format(number(value))
                }
                dateValue={dateText}
                safeFileUrl={(name) =>
                  `${
                    appConstants.baseURL
                  }quotation/viewUploadedQuotationDocument?fileName=/${encodeURIComponent(
                    name || ""
                  )}`
                }
              />
            </div>
          </main>
        </div>
      </div>
    );
  }
  return renderToStaticMarkup(content);
}
